import {QueryClient} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import {DateRange} from "react-day-picker";

import {Authorisation} from "@/lib/auth/components/auth-provider";
import {PERMISSIONS, ROLES} from "@/lib/auth/roles";
import {
  consumerDutyScoreQueryOptions,
  recommendationsPerClientQueryOptions,
} from "@/lib/elevation/hooks";
import DateFilter from '@/lib/filters/date/date-filter'
import {getGuideYear} from "@/lib/filters/date/date-utils";
import {activeAdvisersNumberQueryOptions} from "@/lib/hooks/active-advisers";
import {topRatedAdvisersCountQueryOptions} from "@/lib/hooks/top-rated-advisers-count";
import {isCurrentPage} from "@/lib/utils";
import Elevation from "@/modules/elevation";
import {opportunitiesNumberQueryOptions} from "@/modules/elevation/hooks";
import NoElevationStatistics from "@/modules/elevation/no-elevation-statistics";
import ServiceTypeSelector from '@/modules/elevation-dashboard/components/service-type-selector'
import ElevationVerified from "@/modules/elevation-verified";
import {
  overallRatingQueryOptions,
  qualityOfAdviceRatingQueryOptions,
  qualityOfServiceRatingQueryOptions,
  reviewInvitationStatsQueryOptions,
  reviewsNumberQueryOptions,
  valueForMoneyRatingQueryOptions
} from "@/modules/elevation-verified/hooks";
import Verified from "@/modules/verified";
import {
  influencedAndRecommendersQueryOptions,
  potentialClientsQueryOptions
} from "@/modules/verified/hooks";
import NoVerifiedStatistics from "@/modules/verified/no-verified-statistics";
import {DataLoader} from "@/types/router-context";

const path = '/_auth/'
export const Route = createFileRoute(path)({
  component: Index,
  loader: ({ context: { queryClient, date, roles, identity, serviceType } }: DataLoader) => {
    if (isCurrentPage(path)) {
      return
    }
    prefetchHomeElevetionVerified(queryClient, date)
    if (identity?.showVerifiedStatistics) {
      prefetchHomeVerified(queryClient, date)
    }
    if ((roles ?? []).includes(ROLES.ROLE_ELEVATION)) {
      prefetchHomeElevation(queryClient, date, serviceType)
    }
  },
  beforeLoad: () => {
    return {
      children: <>
        <ServiceTypeSelector />
        <DateFilter />
      </>
    }
  },
})

function Index() {
  return (
    <div className={'flex flex-col items-center justify-center'}>
      <ElevationVerified />
      <Authorisation roles={[ROLES.ROLE_ELEVATION]} permissions={[]} forbiddenFallback={<NoElevationStatistics />}>
        <Elevation />
      </Authorisation>
      <Authorisation roles={[]} permissions={[PERMISSIONS.SHOW_VERIFIED_STATISTICS]} forbiddenFallback={<NoVerifiedStatistics />}>
        <Verified />
      </Authorisation>
    </div>
  )
}

export function prefetchHomeElevetionVerified(queryClient: QueryClient, date: DateRange) {
  queryClient.prefetchQuery(activeAdvisersNumberQueryOptions())
  queryClient.prefetchQuery(reviewInvitationStatsQueryOptions(date))
  queryClient.prefetchQuery(reviewsNumberQueryOptions(date))
  queryClient.prefetchQuery(overallRatingQueryOptions(date))
  queryClient.prefetchQuery(qualityOfAdviceRatingQueryOptions(date))
  queryClient.prefetchQuery(qualityOfServiceRatingQueryOptions(date))
  queryClient.prefetchQuery(valueForMoneyRatingQueryOptions(date))
}

export function prefetchHomeVerified(queryClient: QueryClient, date: DateRange) {
  queryClient.prefetchQuery(potentialClientsQueryOptions(date))
  queryClient.prefetchQuery(influencedAndRecommendersQueryOptions(date))
  queryClient.prefetchQuery(topRatedAdvisersCountQueryOptions(getGuideYear()))
}

export function prefetchHomeElevation(queryClient: QueryClient, date: DateRange, serviceType: number | null) {
  if (serviceType) {
    queryClient.prefetchQuery(opportunitiesNumberQueryOptions(date, serviceType))
    queryClient.prefetchQuery(consumerDutyScoreQueryOptions(date, serviceType, null))
    queryClient.prefetchQuery(recommendationsPerClientQueryOptions(date, serviceType, null))
  }
}